import { useEffect, useRef } from 'react';
import api from '../api';

/**
 * PendoTracker Component
 *
 * Installs Pendo analytics at the highest level of the app.
 * Passes visitor ID, name, and account (selected client) metadata
 * so Marketing can identify users and track traffic across clients.
 */
const PendoTracker = () => {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const publicAppId = 'a1e565cd-741e-430d-b5db-3ed9daa6f5f6';

    // Inject the Pendo agent script
    (function (p, e, n, d, o) {
      var v, w, x, y, z;
      o = p[d] = p[d] || {};
      o._q = o._q || [];
      v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track', 'trackAgent'];
      for (w = 0, x = v.length; w < x; ++w)
        (function (m) {
          o[m] =
            o[m] ||
            function () {
              o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
            };
        })(v[w]);
      y = e.createElement(n);
      y.async = !0;
      y.src = 'https://cdn.pendo.io/agent/static/' + publicAppId + '/pendo.js';
      z = e.getElementsByTagName(n)[0];
      z.parentNode.insertBefore(y, z);
    })(window, document, 'script', 'pendo');

    // Fetch user data and initialize Pendo with visitor/account metadata
    const initPendo = async () => {
      try {
        const response = await api.get('/api/user');
        const user = response.data;

        // Fetch the user's clients to determine the account.
        // The Navbar stores the selected client in localStorage;
        // fall back to /api/user-clients if nothing is stored yet.
        const email = user.mail || user.email || '';
        let accountName = localStorage.getItem('selectedClient') || '';

        if (!accountName && email) {
          try {
            const clientsRes = await api.get(`/api/user-clients?email=${encodeURIComponent(email)}`);
            const clients = clientsRes.data.clients || [];
            if (clients.length > 0) {
              accountName = clients[0];
            }
          } catch (clientErr) {
            console.warn('[PENDO] Could not fetch user clients:', clientErr.message);
          }
        }

        // Determine the app context based on the current URL path
        const path = window.location.pathname;
        const isDataStudio =
          path.startsWith('/faster-chat') ||
          path.startsWith('/ecolane-chat') ||
          path.startsWith('/chat') ||
          path.startsWith('/genies') ||
          path.startsWith('/powerbi-dashboard') ||
          path.startsWith('/databricks-dashboard') ||
          path.startsWith('/insights');

        const appName = isDataStudio ? 'AI Data Studio' : 'Transit Tech OS';

        if (window.pendo && window.pendo.initialize) {
          window.pendo.initialize({
            visitor: {
              id: email || user.id || 'anonymous',
              email: email || undefined,
              full_name: user.displayName || undefined,
              role: user.role || undefined,
            },
            account: {
              id: accountName || 'unknown',
              name: accountName || undefined,
            },
          });
        }
      } catch (error) {
        // User not authenticated — initialize anonymously
        if (window.pendo && window.pendo.initialize) {
          window.pendo.initialize({
            visitor: {
              id: 'anonymous',
            },
            account: {
              id: 'anonymous',
            },
          });
        }
      }
    };

    // Wait for the Pendo script to load before initializing
    if (window.pendo) {
      initPendo();
    } else {
      const checkPendo = setInterval(() => {
        if (window.pendo) {
          clearInterval(checkPendo);
          initPendo();
        }
      }, 100);
      // Timeout after 10 seconds
      setTimeout(() => clearInterval(checkPendo), 10000);
    }
  }, []);

  return null;
};

export default PendoTracker;
